import {
  ButtonType,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  InlineAlertType,
  InlineAlertWidget,
} from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CloseAndDismiss } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { RestApiErrorResponse } from '~src/httpClient/RestApiService';
import { FileInput } from '~src/userManagement/components/FileInput';

import { IPackage } from '../models/IPackage';
import { ERROR_CODE_PACKAGE_ALREADY_EXISTS } from '../serverApi/constants/RestApiConstants';
import { PublishPackageService } from '../serverApi/requests/PublishPackage';
import { UpdatePackageService } from '../serverApi/requests/UpdatePackage';

export const UPLOAD_PACKAGE_DIALOG_TEST_ID = 'upload-package-dialog';
export const UPLOAD_PACKAGE_FILE_INPUT_TEST_ID = 'upload-package-file-input';
export const UPLOAD_PACKAGE_UPLOAD_BUTTON_TEST_ID = 'upload-package-upload-button';
export const UPLOAD_PACKAGE_REPLACE_BUTTON_TEST_ID = 'upload-package-replace-button';
export const UPLOAD_PACKAGE_CANCEL_BUTTON_TEST_ID = 'upload-package-cancel-button';

const Phase = {
  Select: 'select',
  Uploading: 'uploading',
  ReplaceConfirm: 'replace-confirm',
  Error: 'error',
} as const;
type Phase = typeof Phase[keyof typeof Phase];

export type UploadPackageDialogProps = CloseAndDismiss<boolean> & {
  // When set, update this package via PUT (manifest must match); otherwise upload a new one.
  targetPackage?: IPackage;
};

export function UploadPackageDialog(props: UploadPackageDialogProps): React.ReactElement {
  const { targetPackage } = props;
  const [file, setFile] = React.useState<File | undefined>(undefined);
  const [phase, setPhase] = React.useState<Phase>(Phase.Select);
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const performUpload = React.useCallback((replace: boolean) => {
    if (!file) {
      return;
    }
    setPhase(Phase.Uploading);
    setErrorMessage('');
    const request = targetPackage?.luid
      ? UpdatePackageService.updatePackage(targetPackage.luid, file)
      : PublishPackageService.publishPackage(file, replace);
    request
      .then(() => {
        props.close(true);
      })
      .catch((err: RestApiErrorResponse) => {
        const code = err?.data?.error?.code;
        if (!targetPackage && code === ERROR_CODE_PACKAGE_ALREADY_EXISTS && !replace) {
          setPhase(Phase.ReplaceConfirm);
          return;
        }
        setPhase(Phase.Error);
        setErrorMessage(err?.data?.error?.detail || Messages.packages_upload_error_generic());
      });
  }, [file, props, targetPackage]);

  const handleFileSelected = React.useCallback((selected: File | undefined) => {
    setFile(selected);
    setErrorMessage('');
  }, []);

  const isUpdate = !!targetPackage;

  return (
    <DialogWidget
      testId={UPLOAD_PACKAGE_DIALOG_TEST_ID}
      title={isUpdate ? Messages.packages_update_dialog_title() : Messages.packages_upload_dialog_title()}
      type={DialogType.NormalWithTitleBar}
      isModal={true}
      showCloseButton={true}
      onCloseRequest={() => props.dismiss()}
      positioningBehavior={DialogPositioningBehavior.Centered}
      dialogWidth={TabDialogWidth.width500px}>
      {renderBody({ phase, file, errorMessage, handleFileSelected, isUpdate })}
      {renderFooter({ phase, file, performUpload, dismiss: () => props.dismiss(), isUpdate })}
    </DialogWidget>
  );
}

function renderBody(args: {
  phase: Phase;
  file: File | undefined;
  errorMessage: string;
  handleFileSelected: (file: File | undefined) => void;
  isUpdate: boolean;
}): React.ReactNode {
  const { phase, file, errorMessage, handleFileSelected, isUpdate } = args;

  if (phase === Phase.ReplaceConfirm) {
    return (
      <InlineAlertWidget
        type={InlineAlertType.Warning}
        children={Messages.packages_upload_replaceConfirmPrompt()}
        testId='upload-package-replace-prompt' />
    );
  }

  if (phase === Phase.Error) {
    return (
      <InlineAlertWidget
        type={InlineAlertType.Error}
        children={errorMessage}
        testId='upload-package-error' />
    );
  }

  if (phase === Phase.Uploading) {
    return <div>{Messages.packages_upload_uploading()}</div>;
  }

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        {isUpdate ? Messages.packages_update_selectFilePrompt() : Messages.packages_upload_selectFilePrompt()}
      </div>
      <FileInput
        accept='.tpkg,application/zip'
        allowFileDrop={true}
        handleFileInput={handleFileSelected}
        selectedFileName={file?.name}
        testId={UPLOAD_PACKAGE_FILE_INPUT_TEST_ID} />
    </>
  );
}

function renderFooter(args: {
  phase: Phase;
  file: File | undefined;
  performUpload: (replace: boolean) => void;
  dismiss: () => void;
  isUpdate: boolean;
}): React.ReactNode {
  const { phase, file, performUpload, dismiss, isUpdate } = args;

  if (phase === Phase.ReplaceConfirm) {
    return (
      <DialogFooterButtonsWidget
        testId='upload-package-footer'
        buttons={[
          {
            buttonType: ButtonType.Outline,
            children: Messages.packages_upload_button_cancel(),
            handleClick: dismiss,
            testId: UPLOAD_PACKAGE_CANCEL_BUTTON_TEST_ID,
          },
          {
            buttonType: ButtonType.Primary,
            children: Messages.packages_upload_button_replace(),
            handleClick: () => performUpload(true),
            testId: UPLOAD_PACKAGE_REPLACE_BUTTON_TEST_ID,
          },
        ]} />
    );
  }

  return (
    <DialogFooterButtonsWidget
      testId='upload-package-footer'
      buttons={[
        {
          buttonType: ButtonType.Outline,
          children: Messages.packages_upload_button_cancel(),
          handleClick: dismiss,
          testId: UPLOAD_PACKAGE_CANCEL_BUTTON_TEST_ID,
        },
        {
          buttonType: ButtonType.Primary,
          children: isUpdate ? Messages.packages_update_button_update() : Messages.packages_upload_button_upload(),
          handleClick: () => performUpload(false),
          disabled: !file || phase === Phase.Uploading,
          testId: UPLOAD_PACKAGE_UPLOAD_BUTTON_TEST_ID,
        },
      ]} />
  );
}

